import type { AdminReportListIssue } from '../api/DigiobcanApi';
import { __ } from '../utils';
import {
  getReportStatusMeta,
  NEUTRAL_CHIP_STYLES,
  reportStatusAvatarSx,
  ReportStatusDot,
} from './AdminReportStatusItem';
import { userInitials, userLabel } from './UserAvatarStack';
// `Chip` z knižnice je od v2 chip na viacnásobný výber (`children`,
// `selected`, `onRemove`), nie MUI štítok. Tu ide vedome MUI komponent:
// štítky kategórií nesú ikonu, tint z `tintedChip` a `data-*` atribúty, na ktorých stojí meranie preplnenia riadku.
// Prepis na `StatusChip` / `FilterChip` patrí k prerobeniu tejto obrazovky
// na `DataTable` a `FilterBar`, nie k výmene shellu.
import MuiChip from '@mui/material/Chip';
import {
  Avatar,
  Box,
  Stack,
  Tooltip,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

/** `gap: 0.5` v MUI spacingu. Meranie potrebuje hodnotu v px. */
const GAP_PX = 4;

/** Vyska stitku podla temy (fontSize 12, padding 5 + 5, lineHeight 1). */
const FALLBACK_CHIP_HEIGHT = 22;

/**
 * Ikonu v `MuiChip` odsadzuje tema o 4 px zlava - hodnota vyladena na 20px MUI
 * ikony, ktore maju bielu plochu v sebe. Bodka stavu ziadnu nema, takze pri
 * 4 px sedi na hrane a stitok je zlava opticky uzsi ako zprava (label ma 8 px).
 * Preto 8 px zlava a rovnaka `-2px` sprava, co da medzeru bodka-text 6 px.
 */
const CHIP_ICON_SPACING_SX = {
  '& .MuiChip-icon': { marginLeft: '8px', marginRight: '-2px' },
};

type Props = {
  /** Nalezy podnetu, ktore prihlaseny admin vidi. */
  issues?: AdminReportListIssue[];
};

const categoryLabel = (issue: AdminReportListIssue) =>
  issue.category_name || issue.category_id;

/**
 * Popis stavu pre tooltip. Doplnok o moznom duplikate visel na stave
 * `possible_duplicate`, ktory mal rovnaky label aj farbu ako `open` ("Podané")
 * - v stitku, kde je farba jediny nositel stavu, by tak dva rozne stavy
 * splynuli. Odvtedy je navrh na duplicitu priznak naleza a nie jeho stav,
 * takze sa doplnok pyta priamo jeho.
 */
const statusLabel = (issue: AdminReportListIssue) => {
  const { title } = getReportStatusMeta(issue.status);
  const status = issue.has_possible_duplicates
    ? `${title} · ${__('možný duplikát')}`
    : title;

  return issue.assignee
    ? `${status} · ${__('rieši')} ${userLabel(issue.assignee)}`
    : status;
};

/** Priemer kolieska riešiteľa v štítku - menší než výška štítku (22 px). */
const ASSIGNEE_AVATAR_PX = 16;

/**
 * Koliesko sedí na mieste bodky stavu, ale je širšie (16 vs 9 px). Odsadenie
 * bodky (8 px zľava) by ho odtlačilo od okraja, takže zľava len 3 px - okraj
 * kolieska potom kopíruje zaoblenie štítku.
 */
const chipAvatarSx = (status: AdminReportListIssue['status']) => ({
  '& .MuiChip-icon': {
    marginLeft: '3px',
    marginRight: '-2px',
    // `Chip` dáva ikone vlastnú farbu (`.MuiChip-icon` s vyššou
    // špecificitou než trieda kolieska), takže iniciály by zošedli. Farby
    // kolieska preto musia prísť cez ten istý selektor.
    ...reportStatusAvatarSx(status),
  },
});

/**
 * Začiatok štítku: bodka stavu, a keď je nález pridelený konkrétnemu kolegovi
 * (moj-presov#17), **namiesto nej** jeho koliesko s iniciálami vo farbe tej
 * istej bodky (`reportStatusAvatarSx`) - stav tak nesie ďalej farba, len
 * k nej pribudne, kto nález rieši. Meno je v tooltipe štítku; vlastný tooltip
 * kolieska by sa s ním bil.
 *
 * `className` sa musí preposlať z rovnakého dôvodu ako pri bodke: `Chip` si
 * ikonu klonuje a pridáva jej `MuiChip-icon`.
 */
const ChipStart: React.FC<{
  issue: AdminReportListIssue;
  className?: string;
}> = ({ issue, className }) =>
  issue.assignee ? (
    <Avatar
      className={className}
      aria-label={userLabel(issue.assignee)}
      sx={{
        ...reportStatusAvatarSx(issue.status),
        flex: 'none',
        width: ASSIGNEE_AVATAR_PX,
        height: ASSIGNEE_AVATAR_PX,
        fontSize: 8,
        fontWeight: 700,
      }}
    >
      {userInitials(issue.assignee)}
    </Avatar>
  ) : (
    <ReportStatusDot className={className} status={issue.status} on='chip' />
  );

/**
 * Kategorie jedneho podnetu ako stitky so stavom naleza vo forme farebnej
 * bodky. Co sa do riadku nezmesti, sa zloz do `+X` s tooltipom - preto sa
 * skutocna sirka meria a nepracuje sa s fixnym poctom stitkov.
 *
 * Oba layouty su zamerne mimo toku (`position: absolute`):
 *  - meraci drzi vzdy vsetky stitky, aby bolo z coho pocitat,
 *  - a ani jeden tak neurcuje sirku bunky. Keby stitky do toku patrili, bunka
 *    tabulky by sa rozsirila na ich plnu sirku (min-content) a namiesto `+X`
 *    by sa objavil vodorovny scrollbar celej tabulky.
 */
const AdminReportCategories: React.FC<Props> = ({ issues = [] }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = React.useState(issues.length);
  const [chipHeight, setChipHeight] = React.useState(FALLBACK_CHIP_HEIGHT);

  const recalculate = React.useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;

    if (!container || !measure) {
      return;
    }

    const chips = Array.from(
      measure.querySelectorAll<HTMLElement>('[data-category-chip]'),
    );
    const available = container.clientWidth;

    // Pri prvom renderi (skryta zalozka, este nezmerany layout) sirku nepozname -
    // vtedy sa nic neprepocitava, ResizeObserver zavola znova.
    if (!chips.length || !available) {
      return;
    }

    setChipHeight(chips[0].offsetHeight || FALLBACK_CHIP_HEIGHT);

    const overflowChip = measure.querySelector<HTMLElement>(
      '[data-overflow-chip]',
    );
    // `+X` v meracom layoute nesie najvyssie mozne cislo, cize najsirsi pripad.
    const overflowWidth = overflowChip ? overflowChip.offsetWidth + GAP_PX : 0;

    let used = 0;
    let fits = 0;

    chips.every((chip, index) => {
      const width = chip.offsetWidth + (fits ? GAP_PX : 0);
      // Poslednemu stitku uz netreba nechavat miesto na `+X`.
      const reserve = index === chips.length - 1 ? 0 : overflowWidth;

      if (used + width + reserve > available) {
        return false;
      }

      used += width;
      fits += 1;
      return true;
    });

    // Aj v najuzsom stlpci sa vzdy ukaze prvy stitok - flexbox mu label skrati
    // na "Doprava a p…", co povie viac ako samotne "+5".
    setVisibleCount(Math.max(fits, 1));
  }, []);

  React.useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    recalculate();

    // Meraci layout sa sleduje tiez - jeho sirka sa meni, kym sa nedotiahne font.
    const observer = new ResizeObserver(recalculate);
    observer.observe(container);
    if (measureRef.current) {
      observer.observe(measureRef.current);
    }

    return () => observer.disconnect();
  }, [issues, recalculate]);

  if (!issues.length) {
    return (
      <Typography variant='body2' color='text.secondary'>
        {'---'}
      </Typography>
    );
  }

  const visible = issues.slice(0, visibleCount);
  const hidden = issues.slice(visibleCount);

  const renderChip = (issue: AdminReportListIssue, measuring = false) => (
    <MuiChip
      key={issue.issue_id}
      {...(measuring ? { 'data-category-chip': true } : {})}
      icon={<ChipStart issue={issue} />}
      label={categoryLabel(issue)}
      sx={{
        ...getReportStatusMeta(issue.status).chipStyles,
        ...(issue.assignee ? chipAvatarSx(issue.status) : CHIP_ICON_SPACING_SX),
        // `minWidth: 0` nie je kozmetika: flex item ma implicitne `min-width:
        // auto`, cize sa nezmensi pod sirku svojho textu. Stitok by potom zabral
        // celu bunku a `+X` by z nej vypadlo prave v pripade, ked na jeho
        // pocitadle najviac zalezi. Meraci layout ale potrebuje prirodzenu
        // sirku, takze tam nie.
        ...(measuring ? {} : { minWidth: 0 }),
      }}
    />
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        height: chipHeight,
        overflow: 'hidden',
      }}
    >
      <Stack
        ref={measureRef}
        aria-hidden
        direction='row'
        gap={0.5}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 'max-content',
          flexWrap: 'nowrap',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {issues.map((issue) => renderChip(issue, true))}
        <MuiChip data-overflow-chip label={`+${issues.length}`} />
      </Stack>

      <Stack
        direction='row'
        gap={0.5}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          maxWidth: '100%',
          flexWrap: 'nowrap',
        }}
      >
        {visible.map((issue) => (
          <Tooltip
            key={issue.issue_id}
            title={`${categoryLabel(issue)} - ${statusLabel(issue)}`}
          >
            {renderChip(issue)}
          </Tooltip>
        ))}

        {!!hidden.length && (
          <Tooltip
            title={
              <Stack sx={{ p: 1, gap: 0.5 }}>
                {hidden.map((issue) => (
                  <Stack
                    key={issue.issue_id}
                    direction='row'
                    alignItems='center'
                    gap={0.5}
                  >
                    <ReportStatusDot status={issue.status} size={8} />
                    <Typography variant='caption'>
                      {categoryLabel(issue)}
                      {' - '}
                      {statusLabel(issue)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            }
          >
            <MuiChip
              label={`+${hidden.length}`}
              // Pocitadlo nie je stav, takze zostava neutralne - inak by sa
              // v riadku citalo ako dalsia kategoria.
              sx={{
                ...NEUTRAL_CHIP_STYLES,
                cursor: 'default',
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

export default AdminReportCategories;
